/**
 * INCSUBNotificationInfoCard.jsx
 *
 * A React component that displays an individual notification card. 
 * It shows a notification image, title, message, and an indicator for unread notifications. 
 * When the card is clicked, the notification is marked as read.
 *
 * @component
 * @example
 * // Usage of INCSUBNotificationInfoCard component
 * <INCSUBNotificationInfoCard notification={notificationData} index={1} handleRead={handleMarkAsRead} />
 * 
 * @author Govind Dindewad
 */

import React from 'react';
import './notificationCard.css';

/**
 * INCSUBNotificationInfoCard Component
 *
 * This component displays a notification card with an image, title, and message.
 * It also has a visual indicator for unread notifications.
 * When clicked, it triggers a callback to mark the notification as read.
 *
 * @param {Object} props - React props
 * @param {Object} props.notification - The notification data to display
 * @param {number} props.index - The index of the notification in the list (used for alternating background colors)
 * @param {Function} props.handleRead - The callback function to handle marking the notification as read
 * @returns {JSX.Element} Rendered notification card
 * 
 * @author Govind Dindewad
 */
function INCSUBNotificationInfoCard({ notification, index, handleRead }) {
    // Return a div representing the notification card
    return (
        <div
            className='INCSUBNotifiacation_card' 
            // When the notification card is clicked, the handleRead function is triggered to mark the notification as read
            onClick={() => handleRead(notification.notificationId)} 
            // Alternate background color for each card based on its index
            style={{
                background: index % 2 === 0 ? 'rgba(244, 248, 255, 1)' : '#fff',
            }}
        >
            {/* Notification Image */}
            <div className='INCSUBNotifiacation_card_img'>
                <div className='INCSUBNotification_image_holder'>
                    {/* Display the notification image or fallback to a default image if not available */}
                    <img
                        src={notification && notification.notificationImage ? notification.notificationImage : '/assets/icons/common/notificationImage.svg'}
                        alt='notification'
                    />
                </div>
            </div>

            {/* Notification Text Section */}
            <div className='INCSUBNotifiacation_card_text_ctn'>
                {/* Notification Title */}
                <div className='INCSUBNotifiacation_card_type'>
                    {notification?.notificationTitle}
                </div>
                {/* Notification Message */}
                <div className='INCSUBNotifiacation_card_type_desc'>
                    {notification?.notificationMessage}<span style={{}}>{notification?.notificationSentTime}</span>
                </div>
            </div>

            {/* If the notification is unread, display a red dot indicator */}
            {!notification?.read && <div className='INCSUBNotifiacation_card_read_dot'></div>}
        </div>
    );
}

export default INCSUBNotificationInfoCard;
